import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { Fragment } from "react/jsx-runtime";

import HomeIcon from "src/core/assets/images/icons/home.svg?react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/core/components/ui/breadcrumb";
import { ROUTE_PATH } from "src/core/constants/routePath";
import { useTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { cn } from "src/core/lib/utils";

export const PageBreadcrumb: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();

  // SAFE: null si route globale
  const paths = useTenantPaths();

  const location = useLocation();
  const params = useParams();

  const getBreadcrumbItems = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const items: {
      label: React.ReactNode;
      href: string;
      isCurrentPage: boolean;
      isClickable: boolean;
    }[] = [];

    const homeHref = paths?.home() ?? "/";

    // Toujours ajouter l'accueil
    items.push({
      label: (
        <div className="flex flex-row items-center gap-10">
          <div className="flex size-30 items-center justify-center border border-border rounded-lg cursor-pointer text-foreground">
            <HomeIcon />
          </div>
          {location.pathname === ROUTE_PATH.ERROR_404 || location.pathname === ROUTE_PATH.ERROR_500
            ? t("home.title")
            : ""}
        </div>
      ),
      href: homeHref,
      isCurrentPage: false,
      isClickable: true,
    });

    // Ajouter les autres segments selon le chemin
    // NOTE DEV: uniquement si on est dans un contexte tenant (paths != null)
    if (paths && pathSegments.includes("import")) {
      items.push({
        label: t("import.title"),
        href: paths.import(),
        isCurrentPage: true,
        isClickable: false,
      });
    }

    if (paths && pathSegments.includes("folders")) {
      // Si nous sommes sur une page de dossier spécifique
      if (params.id) {
        items.push({
          label: t("folder.title"),
          href: paths.folder(params.id as string),
          isCurrentPage: false,
          isClickable: false,
        });
      } else {
        items.push({
          label: t("folders.title"),
          href: paths.folders(),
          isCurrentPage: location.pathname === paths.folders(),
          isClickable: true,
        });
      }
    }

    return items;
  };

  const items = getBreadcrumbItems();

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        {items.map((item, index) => (
          <Fragment key={item.href}>
            <BreadcrumbItem>
              {item.isCurrentPage || !item.isClickable ? (
                <BreadcrumbPage className={cn(!item.isClickable && "text-muted")}>
                  {item.label}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < items.length - 1 && <BreadcrumbSeparator />}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
